<template>
  <div class="new-online-course">
    <page-header
      title="Curso Online"
      subtitle="Siga as etapas a seguir para cadastrar seu produto."
    />
    <page-container aside>
      <template #aside>
        <snow-steps v-model="currentStep" :steps="steps" vertical />
      </template>
      <template #default>
        <div v-if="currentStep === 2">
          <basic-information-add :form="form" :errors="errors" />
        </div>
        <div v-if="currentStep === 3">
          <h2>Upload de uma imagem</h2>
          <div class="fs-14 text-neutral-400 mb-7">
            Essa imagem aparecerá para você na listagem de produtos e no
            checkout para seus compradores
          </div>
          <upload-image :form="form" />
        </div>
        <div v-show="currentStep === 4">
          <pricing :form="form" :errors="errors" />
        </div>
        <div v-show="currentStep === 5">
          <offers
            ref="offers"
            :currentProduct="form"
            :formErrors="errors"
            :showLoading="loading"
            @hasOffer="e => (hasOffer = e)"
          />
        </div>
        <div
          class="d-flex flex-column flex-md-row justify-content-between my-9 px-3"
        >
          <bp-button
            color="neutral"
            class="order-1 order-md-0"
            variant="filled-1"
            @click="back"
            :icon="currentStep !== 1 ? 'fa-light fa-angle-left' : ''"
            :label="currentStep === 1 ? 'Cancelar' : 'Voltar'"
          >
          </bp-button>
          <bp-button
            :color="isValid ? 'primary' : 'neutral'"
            :disabled="!isValid"
            :trailingIcon="
              currentStep !== steps.length ? 'fa-light fa-angle-right' : ''
            "
            :label="currentStep === steps.length ? 'Salvar Produto' : 'Próximo'"
            @click="next"
          >
          </bp-button>
          <div class="d-md-none my-3"></div>
        </div>
      </template>
    </page-container>
  </div>
</template>

<script>
import { formFields } from "@/functions/helpers.js";

import BasicInformationAdd from "@/components/product/BasicInformationAdd";
import Offers from "@/components/product/Offers";
import Pricing from "@/components/product/Pricing";
import UploadImage from "@/components/product/UploadImage";
import SnowSteps from "@/components/snow-ui/SnowSteps";
import NewProductMixin from "@/mixins/newProduct";
import UnsavedChangesMixin from "@/mixins/unsavedChanges.js";

export default {
  mixins: [NewProductMixin, UnsavedChangesMixin],
  components: {
    BasicInformationAdd,
    Pricing,
    UploadImage,
    Offers,
    SnowSteps
  },
  mounted() {
    this.getCategories();
  },
  data() {
    return {
      currentStep: 2,
      loading: false,
      hasOffer: false,
      steps: [
        "Tipo de produto",
        "Informações básicas",
        "Upload Imagem",
        "Precificação",
        "Cadastrar oferta"
      ],
      ...formFields(
        [
          "name",
          "description",
          "category_id",
          "charge_type",
          "currency",
          "price",
          "price_usd",
          "price_eur",
          "warranty",
          "start_at",
          "end_at",
          "type_product",
          "payment_methods_ids",
          "installments",
          "is_tax_to_producer",
          "smart_installment",
          "option_form",
          "pixel",
          "billet_installments",
          "billet_apply_interest"
        ],
        {
          charge_type: "unique",
          currency: "BRL",
          smart_installment: false,
          type_product: "online_course",
          payment_methods_ids: [],
          pixel: [],
          is_tax_to_producer: false,
          option_form: {
            request_address_shopper: false,
            request_cpf_cnpj_shopper: true,
            request_phone_shopper: false,
            coupon_shopper: false,
            instagram_shopper: false,
            request_zip_code: false,
            pos_address_shopper: false
          },
          billet_installments: 1,
          billet_apply_interest: false
        }
      )
    };
  },
  computed: {
    isValid() {
      const stepsValidation = {
        1: true,
        2: !!(
          this.form.name &&
          this.form.name.length <= 64 &&
          this.form.description &&
          this.form.description.length <= 980 &&
          this.form.category_id
        ),
        3: true,
        4: !!(
          this.form.currency &&
          this.form.price &&
          this.form.price >= 4 &&
          (!parseFloat(this.form.price_eur) || this.form.price_eur >= 4) &&
          (!parseFloat(this.form.price_usd) || this.form.price_usd >= 4) &&
          this.form.payment_methods_ids &&
          this.form.warranty
        )
      };
      if (this.currentStep === 5) {
        return (
          this.hasOffer &&
          (Object.values(stepsValidation).find(v => !v) ?? true)
        );
      }
      return stepsValidation[this.currentStep];
    }
  },
  watch: {
    currentStep(v) {
      if (v === 1) {
        this.$router.push({
          name: "product-new"
        });
      }
    }
  },
  methods: {
    async onSubmit() {
      const offers = this.$refs.offers.offers;
      const data = { ...this.form };

      if (
        !data.payment_methods_ids.filter(id => [1, 3, 34].includes(id)).length
      ) {
        data.installments = 1;
        data.smart_installment = false;
        data.is_tax_to_producer = false;
      }

      if (data.payment_methods_ids.includes("parceled_billet")) {
        data.payment_methods_ids = [2];
      } else {
        data.billet_installments = 1;
        data.billet_apply_interest = false;
      }

      if (!offers) return;

      await this.submitProduct(data, true);

      await Promise.all(
        offers.map(offer => {
          this.$store.dispatch("offer/post", {
            product_id: this.product.id,
            status: this.status ? "active" : "inactive",
            ...offer,
            price_eur: offer.price_eur === 0 ? "" : offer.price_eur,
            price_usd: offer.price_usd === 0 ? "" : offer.price_usd,
            order_bumps: offer.order_bumps.map(({ offer_id }) => ({
              offer_id: offer_id
            }))
          });
        })
      );
    },
    changeStep(step) {
      this.currentStep = step;
    },
    back() {
      if (this.currentStep === 1) {
        this.$router.go(-1);
      } else {
        this.currentStep--;
      }
    },
    next() {
      if (this.currentStep === this.steps.length) {
        this.onSubmit();
      } else {
        this.currentStep++;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.view-product-register {
  ::v-deep {
    .icon-angle-right,
    .icon-angle-left {
      font-size: 18px;
      margin-bottom: 1px;
      font-weight: 900;
    }
  }
}
</style>
